/**
 * Widget API payloads (AGENTS.md 9.1). Shared by the chat app and Cloud Functions.
 * Never duplicate these types elsewhere (section 17).
 */
import { z } from "zod";
import { idSchema, localeSchema } from "../common.js";
import { eventTypeSchema } from "../events.js";
import { formSchemaV1 } from "../form/index.js";
import { pageFormSettingsSchema } from "../firestore/form.js";
export const deviceSchema = z.enum(["mobile", "tablet", "desktop"]);
/** Page context the loader passes to the iframe and the iframe sends to the API. */
export const pageContextSchema = z.object({
    pageUrl: z.string().max(2048).optional(),
    referrer: z.string().max(2048).optional(),
    utm: z.record(z.string().max(40), z.string().max(200)).optional(),
    language: z.string().max(16).optional(),
    device: deviceSchema.optional(),
    visitId: z.string().max(64).optional(),
});
export const publicAgentSchema = z.object({
    id: z.string(),
    name: z.string(),
    title: z.string().optional(),
    photoUrl: z.string().optional(),
    online: z.boolean(),
});
export const publicWidgetSettingsSchema = z.object({
    showOn: z.enum(["all", "mobile", "desktop"]),
    delayMs: z.number().int(),
    scrollPercent: z.number().int(),
    offset: z.object({ x: z.number().int(), y: z.number().int() }),
    animation: z.enum(["ring", "none"]),
    cta: z.object({
        enabled: z.boolean(),
        text: z.string(),
        imageUrl: z.string().optional(),
        delayMs: z.number().int(),
        showOncePerVisit: z.boolean(),
        dismissible: z.boolean(),
    }),
    pages: z.object({ include: z.array(z.string()), exclude: z.array(z.string()) }),
    hours: z.object({
        enabled: z.boolean(),
        timezone: z.string(),
        weekly: z.record(z.string(), z.array(z.object({ from: z.string(), to: z.string() }))),
        holidays: z.array(z.string()),
        awayMessage: z.string(),
    }),
    agents: z.object({
        routing: z.enum(["off", "category", "page", "round_robin", "choose"]),
        list: z.array(publicAgentSchema),
    }),
    countries: z.object({ mode: z.enum(["off", "allow", "block"]), list: z.array(z.string()) }),
    qrOnDesktop: z.boolean(),
    interceptWaLinks: z.boolean(),
    pageOverrides: z.array(z.object({
        pattern: z.string(),
        buttonLabel: z.string().optional(),
        ctaText: z.string().optional(),
    })),
    tracking: z.object({
        dataLayer: z.boolean(),
        ga4: z.boolean(),
        metaPixel: z.boolean(),
        googleAds: z.object({
            conversionId: z.string().optional(),
            label: z.string().optional(),
            countOn: z.array(z.enum(["lead_completed", "whatsapp_open"])).optional(),
        }),
    }),
});
// ---- GET /v1/widget/config ----
export const widgetConfigResponseSchema = z.object({
    clinicId: z.string(),
    clinic: z.object({
        name: z.string(),
        slug: z.string(),
        logoUrl: z.string().optional(),
        defaultLocale: localeSchema,
        /** Set when the business accepts "send by email" instead of WhatsApp. */
        contactEmail: z.string().optional(),
    }),
    branding: z.object({
        color: z.string(),
        buttonPosition: z.enum(["right", "left"]),
        buttonLabel: z.string(),
        buttonIcon: z.string(),
        showBadge: z.boolean(),
    }),
    /** "active": full widget. "fallback": plain wa.me link only (section 13). */
    mode: z.enum(["active", "fallback"]),
    whatsappNumber: z.string(),
    booking: z.object({
        type: z.enum(["none", "external", "internal"]),
        externalUrl: z.string().optional(),
        externalLabel: z.string().optional(),
    }),
    form: z
        .object({
        formId: z.string(),
        formVersionId: z.string(),
        schema: formSchemaV1,
        /** Present when a specific funnel was asked for (`form=`), e.g. by a page form. */
        name: z.string().optional(),
        pageForm: pageFormSettingsSchema.optional(),
    })
        .optional(),
    procedures: z.array(z.object({ id: z.string(), name: z.string(), category: z.string() })),
    /** Cloudflare Turnstile for page forms, when the deployment checks it (16 October 2026). */
    challenge: z.object({ sitekey: z.string(), action: z.string() }).optional(),
    analytics: z.object({ journeyBeforeConsent: z.boolean() }),
    typingDelayMs: z.number().int().min(0).max(3000),
    /** Section 14b button behaviour; agents carry no phone numbers (routing is server-side). */
    widget: publicWidgetSettingsSchema,
});
// ---- POST /v1/widget/sessions ----
export const createSessionRequestSchema = z.object({
    clinicId: z.string().min(1).max(64),
    formId: z.string().min(1).max(64),
    formVersionId: z.string().min(1).max(64),
    consentNodeId: idSchema,
    consent: z.record(z.string().max(40), z.boolean()),
    locale: localeSchema,
    context: pageContextSchema.optional(),
    /** Bot challenge token (Turnstile). Required once the clinic enables it. */
    challengeToken: z.string().max(4096).optional(),
});
export const createSessionResponseSchema = z.object({
    sessionToken: z.string(),
    leadId: z.string(),
    ref: z.string(),
    expiresAt: z.string(),
});
// ---- POST /v1/widget/form-submit (page forms, 16 October 2026) ----
/**
 * A funnel shown as a classic form on a page sends every answer at once, with the consent
 * answer among them. The server validates each against the published version (only questions
 * that are visible for these answers count) and creates the lead in one step.
 */
export const formSubmitRequestSchema = z.object({
    clinicId: z.string().min(1).max(64),
    formId: z.string().min(1).max(64),
    formVersionId: z.string().min(1).max(64),
    answers: z.record(idSchema, z.unknown()),
    locale: localeSchema,
    context: pageContextSchema.optional(),
    /** Cloudflare Turnstile token, checked when the server has a secret configured. */
    challengeToken: z.string().max(4096).optional(),
});
// ---- PATCH /v1/widget/leads/current/answers ----
export const upsertAnswerRequestSchema = z.object({
    nodeId: idSchema,
    value: z.unknown(),
    /** Client timestamp, informational only. */
    answeredAt: z.string().optional(),
    msToAnswer: z.number().int().min(0).max(86_400_000).optional(),
});
export const upsertAnswerResponseSchema = z.object({
    ok: z.literal(true),
    value: z.unknown(),
    invalidated: z.array(z.string()),
    completed: z.boolean(),
    nextNodeId: z.string().optional(),
});
// ---- POST /v1/widget/leads/current/uploads ----
export const UPLOAD_CONTENT_TYPES = [
    "image/jpeg",
    "image/png",
    "image/heic",
    "image/heif",
    "image/webp",
    "application/pdf",
];
export const createUploadRequestSchema = z.object({
    nodeId: idSchema,
    contentType: z.enum(UPLOAD_CONTENT_TYPES),
    size: z.number().int().min(1),
    originalName: z.string().max(255).optional(),
});
export const createUploadResponseSchema = z.object({
    fileId: z.string(),
    uploadUrl: z.string(),
    method: z.literal("PUT"),
    headers: z.record(z.string(), z.string()),
    expiresAt: z.string(),
});
// ---- POST /v1/widget/leads/current/handoff ----
export const handoffRequestSchema = z.object({
    type: z.enum(["whatsapp", "booking", "email"]),
    atNodeId: idSchema.optional(),
    /** Chosen support agent when routing is "choose" (section 14b). */
    agentId: z.string().max(40).optional(),
});
export const handoffResponseSchema = z.object({
    type: z.enum(["whatsapp", "booking", "email"]),
    message: z.string().optional(),
    url: z.string(),
    label: z.string().optional(),
    ref: z.string(),
});
// ---- POST /v1/widget/leads/current/handoff-link ----
/**
 * "Continue on your phone" (section 14b, desktop): a short-lived link for a QR code. The phone
 * opens `<api>/wa/<token>`, which redirects to WhatsApp with the full message. The QR never
 * carries the message itself (too long to scan, and it holds patient answers).
 */
export const handoffLinkRequestSchema = z.object({
    atNodeId: idSchema.optional(),
    agentId: z.string().max(40).optional(),
});
/** Minutes a "continue on your phone" link stays valid. */
export const HANDOFF_LINK_MINUTES = 15;
// ---- GET /v1/widget/leads/current ----
export const resumeResponseSchema = z.object({
    leadId: z.string(),
    ref: z.string(),
    formId: z.string(),
    formVersionId: z.string(),
    answers: z.record(z.string(), z.unknown()),
    files: z.array(z.object({
        fileId: z.string(),
        nodeId: z.string(),
        contentType: z.string(),
        size: z.number(),
        verified: z.boolean(),
    })),
    completed: z.boolean(),
    whatsappOpened: z.boolean(),
    expiresAt: z.string(),
    /** The lead's own form version, so a conversation continues after the clinic publishes a new one. */
    schema: formSchemaV1.optional(),
    /** A live booking made in this conversation (section 11). */
    appointment: z
        .object({
        typeName: z.string(),
        doctorName: z.string(),
        startAt: z.string(),
        endAt: z.string(),
        timezone: z.string(),
        mode: z.enum(["online", "in_person"]),
        status: z.enum(["held", "confirmed", "cancelled", "completed", "no_show"]),
    })
        .optional(),
});
// ---- POST /v1/widget/events ----
export const widgetEventSchema = z.object({
    type: eventTypeSchema,
    at: z.string(),
    clinicId: z.string().min(1).max(64),
    visitId: z.string().max(64).optional(),
    pagePath: z.string().max(1024).optional(),
    device: deviceSchema.optional(),
    locale: z.string().max(16).optional(),
    formId: z.string().max(64).optional(),
    formVersionId: z.string().max(64).optional(),
    utm: z.record(z.string().max(40), z.string().max(200)).optional(),
    /** Ids, types and timings only. Never answer values (9.4). */
    data: z
        .object({
        trigger: z.string().max(40).optional(),
        stepId: idSchema.optional(),
        nodeId: idSchema.optional(),
        lastNodeId: idSchema.optional(),
        atNodeId: idSchema.optional(),
        nodeType: z.string().max(40).optional(),
        msToAnswer: z.number().int().min(0).optional(),
        secondsOpen: z.number().int().min(0).optional(),
        sequence: z.number().int().min(0).optional(),
        early: z.boolean().optional(),
        reason: z.string().max(60).optional(),
    })
        .strict()
        .optional(),
});
export const eventsBatchRequestSchema = z.object({
    events: z.array(widgetEventSchema).min(1).max(50),
});
// ---- errors ----
export const apiErrorSchema = z.object({
    error: z.string(),
    message: z.string().optional(),
    details: z.unknown().optional(),
});
//# sourceMappingURL=widget.js.map